import type { ComponentConsumptionDto, StyleOperationNBLC } from "@/api/model";

export interface ComponentInfo {
  name: string;
  consumption: number | null | undefined;
}

export interface HeadTypeInfo {
  name: string;
  heads: number;
  components: ComponentInfo[];
}

export interface ComponentDisplayRow {
  isHead: boolean;
  displayName: string;
  headPosition: number;
  heads: number;
  leafIndex: number;
  divisor: number;
  consumption?: number | null;
}

const singularWords: Record<string, string> = {
  needles: "Needle",
  loopers: "Looper",
  covers: "Cover",
  bobbins: "Bobbin"
};

// Head type names are plural for multi-part heads ("Flatlock Needles"); only the last word is changed.
export function toSingularName(headName: string): string {
  const words = headName.trim().split(/\s+/);
  const singular = singularWords[(words.at(-1) ?? "").toLowerCase()];
  if (singular) {
    words[words.length - 1] = singular;
  }
  return words.join(" ");
}

export function toHeadTypes(components: ComponentConsumptionDto[] | null | undefined): HeadTypeInfo[] {
  const headTypes: HeadTypeInfo[] = [];
  if (!components) {
    return headTypes;
  }

  for (const component of components) {
    const headName = component.headName || "";

    let head = headTypes.find(h => h.name === headName);
    if (!head) {
      head = { name: headName, heads: 0, components: [] };
      headTypes.push(head);
    }
    head.heads = head.heads + 1;
    head.components.push({ name: component.name || "", consumption: component.consumption });
  }
  return headTypes;
}

export function buildLeafRows(
  headTypes: HeadTypeInfo[],
  savedRows: StyleOperationNBLC[] | null | undefined,
  expanded: boolean
): StyleOperationNBLC[] {
  const rows: StyleOperationNBLC[] = [];

  for (const head of headTypes) {
    const showParts = expanded && head.heads > 1;

    let rowCount = 1;
    if (showParts) {
      rowCount = head.heads;
    }

    let savedForHead: StyleOperationNBLC[] = [];
    if (savedRows) {
      savedForHead = savedRows.filter(r => (r.needleName || "").trim() === head.name);
    }

    // rows saved with a part number go back on that part; a part whose row was removed stays empty
    const isNumbered = showParts && savedForHead.some(r => r.componentNo != null);

    for (let i = 0; i < rowCount; i++) {
      let saved: StyleOperationNBLC | undefined = savedForHead[i];
      if (isNumbered) {
        saved = savedForHead.find(r => r.componentNo === i + 1);
      }
      if (saved) {
        rows.push(saved);
      } else {
        rows.push({
          needleName: head.name,
          brandId: null,
          ticketTex: null,
          shade: null,
          packageSize: null
        } as StyleOperationNBLC);
      }
    }
  }
  return rows;
}

export function buildDisplayRows(headTypes: HeadTypeInfo[], expanded: boolean): ComponentDisplayRow[] {
  const rows: ComponentDisplayRow[] = [];
  let leafIndex = 0;

  for (let headPosition = 0; headPosition < headTypes.length; headPosition++) {
    const head = headTypes[headPosition];
    const showParts = expanded && head.heads > 1;

    if (!showParts) {
      rows.push({
        isHead: false,
        displayName: head.name,
        headPosition: headPosition,
        heads: head.heads,
        leafIndex: leafIndex,
        divisor: 1
      });
      leafIndex = leafIndex + 1;
      continue;
    }

    rows.push({
      isHead: true,
      displayName: head.name,
      headPosition: headPosition,
      heads: head.heads,
      leafIndex: -1,
      divisor: 1
    });

    // part rows are named per single part: "Flatlock Needles" -> "Flatlock Needle 1", "Flatlock Needle 2", ...
    const componentName = toSingularName(head.name);
    for (let componentNumber = 1; componentNumber <= head.components.length; componentNumber++) {
      const part = head.components[componentNumber - 1];
      rows.push({
        isHead: false,
        displayName: componentName + " " + componentNumber,
        headPosition: headPosition,
        heads: head.heads,
        leafIndex: leafIndex,
        divisor: head.heads,
        consumption: part.consumption
      });
      leafIndex = leafIndex + 1;
    }
  }
  return rows;
}
